/* istanbul ignore file - need to fix flakey tests */
import { faArrowsTurnRight, faArrowTurnDownRight, faLayerMinus, faPencil } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@partful/shared/ui/Button';
import { useConfirmation } from '@partful/shared/ui/Confirmation/useConfirmation';
import { PopoverMenu, PopoverMenuItem } from '@partful/shared/ui/PopoverMenu';
import { CategoryNode } from '@services/productInventory';
import { MRT_Row, MRT_TableInstance } from 'material-react-table';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePartsCategories } from '../../lib/context/PartsCategoriesContext';
import { getSelectedRows } from '../../lib/utils';

export function PartsCategoriesTableRowActions({
  table,
  row,
  onSelectRowsToMove,
}: {
  table: MRT_TableInstance<CategoryNode>;
  row: MRT_Row<CategoryNode>;
  onSelectRowsToMove: (rows: CategoryNode[]) => void;
}) {
  const { confirm, close } = useConfirmation();
  const { deleteCategory } = usePartsCategories();

  const { t: i18n } = useTranslation('manageProduct');
  const { t: i18nCommon } = useTranslation('common');

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const filteredRows = getSelectedRows(table.getSelectedRowModel().flatRows);
  const showBulkActions = filteredRows.length > 1 && filteredRows.some((selectedRow) => selectedRow.id === row.id);

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={() => {
          setIsPopoverOpen(true);
        }}
        aria-haspopup="true"
        aria-expanded={isPopoverOpen ? 'true' : undefined}
        rounded
      >
        {i18n('partsCategories.interactive.actions')}
      </Button>
      <PopoverMenu
        anchorEl={buttonRef.current}
        open={isPopoverOpen}
        onClose={/* istanbul ignore next - don't need to assert it closes */ () => setIsPopoverOpen(false)}
      >
        <PopoverMenuItem
          key="edit-category"
          data-testid="edit-category"
          disabled={row.original.itemType !== 'Category'}
          onClick={() => {
            table.setEditingRow(row);
            setIsPopoverOpen(false);
          }}
        >
          <FontAwesomeIcon icon={faPencil} />
          {i18n('partsCategories.interactive.editCategory')}
        </PopoverMenuItem>
        <PopoverMenuItem
          key="delete-category"
          data-testid="delete-category"
          disabled={row.original.children?.length !== 0 || row.original.itemType !== 'Category'}
          onClick={() => {
            confirm({
              title: i18n('partsCategories.interactive.deleteConfirmation', {
                name: row.original.name,
              }),
              confirmText: i18nCommon('yesDelete'),
              cancelText: i18nCommon('cancel'),
              message: '',
              onConfirm: async () => {
                table.resetRowSelection();
                deleteCategory(row.original);
                close();
              },
            });
            setIsPopoverOpen(false);
          }}
        >
          <FontAwesomeIcon icon={faLayerMinus} />
          {i18n('partsCategories.interactive.deleteCategory')}
        </PopoverMenuItem>
        <PopoverMenuItem
          key="move-to"
          aria-label="Move to"
          data-testid="move-part-to"
          onClick={() => {
            onSelectRowsToMove([row.original]);
            close();
            setIsPopoverOpen(false);
          }}
        >
          <FontAwesomeIcon icon={faArrowTurnDownRight} />
          {i18n('partsCategories.interactive.moveTo')}
        </PopoverMenuItem>
        {showBulkActions ? (
          <PopoverMenuItem
            key="move-multiple"
            aria-label={`Move selected (${filteredRows.length}) `}
            data-testid="move-multiple"
            onClick={() => {
              onSelectRowsToMove(filteredRows.map((row) => row.original));
              setIsPopoverOpen(false);
            }}
          >
            <FontAwesomeIcon icon={faArrowsTurnRight} />
            {i18n('partsCategories.interactive.moveMultipleTo', { count: filteredRows.length })}
          </PopoverMenuItem>
        ) : null}
      </PopoverMenu>
    </>
  );
}
